// components/Dashboard/Dashboard.jsx  — fully schema-driven + transaction drill-down
import React, { useMemo, useState } from "react";
import { Text, Spinner, Button } from "@fluentui/react-components";
import { ArrowSyncRegular } from "@fluentui/react-icons";
import KPICard from "./KPICard";
import DieselCard from "./DieselCard";
import LogisticsCard from "./LogisticsCard";
import ProgressCard from "./ProgressCard";
import WeeklyHighlightsCard from "./WeeklyHighlightsCard";
import DetailDrawer from "../DataEntry/DetailDrawer";
import { useSchema } from "../../context/SchemaContext";
import { formatDate, safeNum } from "../../utils/helpers";
import { GRADIENT_OPTIONS } from "../../hooks/useEntitySchema";
import { applyDateFilter } from "../../hooks/useDateFilter";
import DateRangeFilter from "../shared/DateRangeFilter";

export default function Dashboard({ records, loading, refresh, getDetail, dateFilter, dieselTransactions = [], logisticsTransactions = [] }) {
  const { dashboardEntities, activeEntities, formatValue } = useSchema();

  const filteredRecords = dateFilter.filtered;
  const latest = filteredRecords[0] || null;
  const prev   = filteredRecords[1] || null;

  // YTD = all records in the current calendar year, independent of the date filter
  const ytdRecords = useMemo(() => {
    const yearStart = new Date(new Date().getFullYear(), 0, 1);
    return records.filter(r => r.weekEnding && new Date(r.weekEnding) >= yearStart);
  }, [records]);

  // Diesel grouped by location — filtered to the visible date range
  const dieselByLocation = useMemo(() => {
    if (!dieselTransactions.length) return [];
    const visibleWeeks = new Set(filteredRecords.map(r => r.weekEnding));
    const inPeriod = filteredRecords.length
      ? dieselTransactions.filter(r => visibleWeeks.has(r.weekEnding))
      : dieselTransactions;
    const map = {};
    inPeriod.forEach(r => {
      const loc = r.location || "Unknown";
      if (!map[loc]) map[loc] = [];
      map[loc].push(r);
    });
    return Object.entries(map).map(([location, recs]) => ({
      location,
      records: recs.sort((a, b) => b.weekEnding.localeCompare(a.weekEnding)),
    }));
  }, [dieselTransactions, filteredRecords]);

  // Diesel YTD totals — supply & consumption (closing stock is point-in-time, not cumulative)
  const dieselYTD = useMemo(() => {
    const yearStart = `${new Date().getFullYear()}-01-01`;
    const ytd = dieselTransactions.filter(r => (r.weekEnding || "") >= yearStart);
    return {
      supply:      ytd.reduce((s, r) => s + safeNum(r.supply), 0),
      consumption: ytd.reduce((s, r) => s + safeNum(r.consumption), 0),
    };
  }, [dieselTransactions]);

  // Logistics grouped by route — filtered to the visible date range
  const logisticsByRoute = useMemo(() => {
    if (!logisticsTransactions.length) return [];
    const visibleWeeks = new Set(filteredRecords.map(r => r.weekEnding));
    const inPeriod = filteredRecords.length
      ? logisticsTransactions.filter(r => visibleWeeks.has(r.weekEnding))
      : logisticsTransactions;
    const map = {};
    inPeriod.forEach(r => {
      const route = r.route || "Unknown";
      if (!map[route]) map[route] = [];
      map[route].push(r);
    });
    return Object.entries(map).map(([route, recs]) => ({
      route,
      records: recs.sort((a, b) => b.weekEnding.localeCompare(a.weekEnding)),
    }));
  }, [logisticsTransactions, filteredRecords]);

  // Detail drawer state
  const [drawerDetail, setDrawerDetail] = useState(null);

  const filterDrawerRows = (rows) =>
    applyDateFilter(rows, dateFilter.preset, dateFilter.customFrom, dateFilter.customTo);

  const handleKpiClick = (entity) => {
    if (!getDetail) return;
    const detail = getDetail(entity.key, null);
    setDrawerDetail({
      ...detail,
      rows: filterDrawerRows(detail.rows),
      metricLabel: entity.name,
      weekLabel: dateFilter.rangeLabel,
    });
  };

  const trend = (curr, prevVal) => {
    if (!prevVal || prevVal === 0 || curr === null || curr === undefined) return null;
    const pct = ((curr - prevVal) / Math.abs(prevVal)) * 100;
    return Math.round(pct);
  };

  const progressEntities = useMemo(() =>
    dashboardEntities.filter((e) => e.type === "percentage"),
    [dashboardEntities]
  );

  const kpiEntities = useMemo(() =>
    dashboardEntities.filter((e) => e.type !== "percentage"),
    [dashboardEntities]
  );

  const kpiByCategory = useMemo(() => {
    const cats = {};
    kpiEntities.forEach((e) => {
      if (!cats[e.category]) cats[e.category] = [];
      cats[e.category].push(e);
    });
    return cats;
  }, [kpiEntities]);

  const textEntities = useMemo(() =>
    activeEntities.filter((e) => e.type === "text"),
    [activeEntities]
  );

  const logisticsEntities = useMemo(() =>
    activeEntities.filter((e) => e.category === "Logistics"),
    [activeEntities]
  );

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
        <Spinner size="large" label="Loading SCM data..." />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <Text weight="bold" size={600} style={{ color: "#201F1E", display: "block" }}>SCM Overview Dashboard</Text>
          {latest && (
            <Text style={{ color: "#605E5C", fontSize: 13 }}>
              Latest week ending: {formatDate(latest.weekEnding)} · {filteredRecords.length} weeks · {dateFilter.rangeLabel} · {dashboardEntities.length} active metrics
            </Text>
          )}
        </div>
        <Button icon={<ArrowSyncRegular />} onClick={refresh} appearance="outline" size="small">Refresh</Button>
      </div>

      {/* Date Range Filter */}
      <DateRangeFilter {...dateFilter} style={{ marginBottom: 24 }} />

      {/* KPI Cards — grouped by category */}
      {Object.entries(kpiByCategory).map(([category, entities]) => (
        <div key={category} style={{ marginBottom: 24 }}>
          <Text weight="semibold" style={{ color: "#605E5C", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, display: "block" }}>
            {category}
          </Text>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 14 }}>
            {entities.map((entity) => {
              // Point-in-time metrics: latest snapshot is meaningful; summing is not
              const isPointInTime = /closing|opening|stock|balance|avg|average/i.test(entity.key);

              // Period cumulative: sum all weeks in the selected range; latest for point-in-time
              const periodVal = isPointInTime
                ? (latest ? latest.fields?.[entity.key] : null)
                : filteredRecords.reduce((acc, r) => acc + safeNum(r.fields?.[entity.key]), 0) || null;

              // Week-over-week trend uses last two individual week values for directional context
              const latestWeekVal = latest ? latest.fields?.[entity.key] : null;
              const prevVal       = prev   ? prev.fields?.[entity.key]   : null;
              const gradient      = GRADIENT_OPTIONS.find((g) => g.key === entity.cardGradient)?.css;

              // YTD only meaningful for flow (cumulative) metrics
              const ytd = isPointInTime
                ? null
                : ytdRecords.reduce((acc, r) => acc + safeNum(r.fields?.[entity.key]), 0);

              const subtitle = entity.type === "text"
                ? ""
                : isPointInTime
                  ? (latest?.weekEnding ? `As of ${formatDate(latest.weekEnding)}` : "")
                  : `YTD: ${formatValue(entity, ytd)}`;

              return (
                <KPICard
                  key={entity.id}
                  title={entity.name}
                  value={formatValue(entity, periodVal)}
                  subtitle={subtitle}
                  icon={entity.icon}
                  gradient={gradient}
                  trend={trend(safeNum(latestWeekVal), safeNum(prevVal))}
                  trendLabel="vs prev week"
                  onClick={getDetail ? () => handleKpiClick(entity) : undefined}
                />
              );
            })}
          </div>
        </div>
      ))}

      {/* Diesel — YTD supply/consumption summary + one card per location */}
      {dieselByLocation.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Text weight="semibold" style={{ color: "#605E5C", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, display: "block" }}>
            ⛽ Fuel / Diesel Inventory — by Location
          </Text>
          {/* YTD supply & consumption summary strip */}
          <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            {[
              { label: "YTD Total Supply",      value: `${dieselYTD.supply.toLocaleString()} L`,      color: "#107C10", bg: "#F0FDF4" },
              { label: "YTD Total Consumption", value: `${dieselYTD.consumption.toLocaleString()} L`, color: "#D83B01", bg: "#FFF4F0" },
            ].map(stat => (
              <div key={stat.label} style={{ background: stat.bg, borderRadius: 10, padding: "10px 18px", border: `1.5px solid ${stat.color}30`, display: "flex", alignItems: "center", gap: 10 }}>
                <div>
                  <Text style={{ fontSize: 18, fontWeight: 800, color: stat.color, display: "block", lineHeight: 1.2 }}>{stat.value}</Text>
                  <Text style={{ fontSize: 11, color: "#605E5C", fontWeight: 500 }}>{stat.label}</Text>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
            {dieselByLocation.map(({ location, records: locRecs }) => (
              <div
                key={location}
                onClick={getDetail ? () => {
                  const detail = getDetail("dieselClosingStock", null);
                  const locRows = detail.rows.filter(r => (r.location || "Unknown") === location);
                  setDrawerDetail({
                    ...detail,
                    rows: filterDrawerRows(locRows),
                    metricLabel: `Diesel — ${location}`,
                    weekLabel: dateFilter.rangeLabel,
                  });
                } : undefined}
                style={{ cursor: getDetail ? "pointer" : "default" }}
                onMouseEnter={getDetail ? (e) => { e.currentTarget.firstChild.style.boxShadow = "0 6px 20px rgba(0,0,0,0.14)"; } : undefined}
                onMouseLeave={getDetail ? (e) => { e.currentTarget.firstChild.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)"; } : undefined}
              >
                <DieselCard location={location} records={locRecs} />
                {getDetail && (
                  <Text style={{ fontSize: 10, color: "#9CA3AF", display: "block", textAlign: "center", marginTop: 4 }}>
                    🔍 click for details
                  </Text>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Logistics — one card per route */}
      {logisticsByRoute.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Text weight="semibold" style={{ color: "#605E5C", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, display: "block" }}>
            🚚 Logistics — by Route
          </Text>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
            {logisticsByRoute.map(({ route, records: routeRecs }) => (
              <div
                key={route}
                onClick={getDetail ? () => {
                  const detail = getDetail("uyoLogisticsTrips", null);
                  const routeRows = detail.rows.filter(r => (r.route || "Unknown") === route);
                  setDrawerDetail({
                    ...detail,
                    rows: filterDrawerRows(routeRows),
                    metricLabel: `Logistics — ${route}`,
                    weekLabel: dateFilter.rangeLabel,
                  });
                } : undefined}
                style={{ cursor: getDetail ? "pointer" : "default" }}
                onMouseEnter={getDetail ? (e) => { e.currentTarget.firstChild.style.boxShadow = "0 6px 20px rgba(0,0,0,0.14)"; } : undefined}
                onMouseLeave={getDetail ? (e) => { e.currentTarget.firstChild.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)"; } : undefined}
              >
                <LogisticsCard route={route} records={routeRecs} />
                {getDetail && (
                  <Text style={{ fontSize: 10, color: "#9CA3AF", display: "block", textAlign: "center", marginTop: 4 }}>
                    🔍 click for details
                  </Text>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Project Progress row (schema-driven logistics summary removed — now using transaction-level LogisticsCards above) */}
      {(progressEntities.length > 0 || logisticsEntities.length > 0) && (
        <div style={{ marginBottom: 24 }}>
          <Text weight="semibold" style={{ color: "#605E5C", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10, display: "block" }}>
            Logistics & Project Progress
          </Text>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
            {/* Logistics summary — clickable */}
            {logisticsEntities.length > 0 && (
              <div
                onClick={getDetail ? () => {
                  const detail = getDetail("uyoLogisticsTrips", null);
                  setDrawerDetail({ ...detail, rows: filterDrawerRows(detail.rows), metricLabel: "Logistics Records", weekLabel: dateFilter.rangeLabel });
                } : undefined}
                style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #EDEBE9", cursor: getDetail ? "pointer" : "default", transition: "box-shadow 0.12s" }}
                onMouseEnter={getDetail ? (e) => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.12)"; } : undefined}
                onMouseLeave={getDetail ? (e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)"; } : undefined}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#008272,#00A695)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>🚚</div>
                  <Text weight="semibold" size={400} style={{ color: "#201F1E" }}>Logistics</Text>
                  {getDetail && <Text style={{ fontSize: 10, color: "#9CA3AF", marginLeft: "auto" }}>🔍 click for details</Text>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10 }}>
                  {logisticsEntities.map((entity) => {
                    const val = latest?.fields?.[entity.key];
                    return (
                      <div key={entity.id} style={{ background: "#F3F2F1", borderRadius: 10, padding: "12px 14px", textAlign: "center" }}>
                        <Text style={{ fontSize: 22, fontWeight: 700, color: "#008272", display: "block" }}>{formatValue(entity, val)}</Text>
                        <Text style={{ fontSize: 11, color: "#605E5C" }}>{entity.name}</Text>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 10, padding: "8px 12px", background: "#F0FFF8", borderRadius: 8 }}>
                  <Text style={{ fontSize: 12, color: "#065F46" }}>
                    Cumulative: {logisticsEntities.map((e) => {
                      const total = filteredRecords.reduce((a, r) => a + safeNum(r.fields?.[e.key]), 0);
                      return `${e.name}: ${formatValue(e, total)}`;
                    }).join(" · ")}
                  </Text>
                </div>
              </div>
            )}

            {/* Progress bars — clickable */}
            {progressEntities.length > 0 && (
              <div
                onClick={getDetail ? () => {
                  const detail = getDetail("uyoPortacabins", null);
                  setDrawerDetail({ ...detail, rows: filterDrawerRows(detail.rows), metricLabel: "Project Updates", weekLabel: dateFilter.rangeLabel });
                } : undefined}
                style={{ cursor: getDetail ? "pointer" : "default" }}
              >
                <ProgressCard
                  title="Project Progress"
                  icon="🏗"
                  items={progressEntities.map((e) => ({
                    label: e.name,
                    value: latest?.fields?.[e.key],
                    color: GRADIENT_OPTIONS.find((g) => g.key === e.cardGradient)?.css?.match(/#[0-9A-Fa-f]{6}/)?.[0] || "#0066FF",
                  }))}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Text notes / highlights */}
      {textEntities.length > 0 && (
        <WeeklyHighlightsCard records={filteredRecords} textEntities={textEntities} />
      )}

      {/* Detail Drawer */}
      <DetailDrawer detail={drawerDetail} onClose={() => setDrawerDetail(null)} />
    </div>
  );
}
